import {
  ChangeCandidate,
  ChangeStatus,
  MatchKind,
  ReviewSection,
  isVerifyFlag,
} from '../../models/v2models/review-state';

// Wire format of GET /api/v2/Reports/{reportId}/review. Kept separate from the view model so
// the screen is not coupled to the API's naming.

export interface ReviewProvenanceDto {
  provenanceId: number;
  changeId: number;
  filename?: string;
  fileName?: string;
  sheetName: string | null;
  cell: string | null;
  columnName: string | null;
  value: string | null;
}

export interface ReviewChangeDto {
  changeId: number;
  sectionId: number;
  candidateType: string;
  source: string;
  rationale?: string | null;
  originalText: string;
  proposedText: string;
  editedText: string | null;
  status: string;
  /** Why the candidate exists, e.g. 'UNCHANGED_REVIEW_REQUIRED'. */
  triggerReason?: string | null;
  dateTime: string;
  updatedDate: string | null;
  provenances: ReviewProvenanceDto[] | null;
}

export interface ReviewSectionDto {
  id: number;
  reportId: number;
  sectionSequence: number;
  parentSectionId: number | null;
  sectionNumber: string;
  sectionTitle: string;
  sectionContent: string;
  status: string;
  isDeleted: boolean;
  sectionScore: number | null;
  updatedBy: string;
  updatedDate: string;
  changes: ReviewChangeDto[] | null;
}

export function mapReviewSections(sections: ReviewSectionDto[]): ReviewSection[] {
  return sections
    .filter((section) => !section.isDeleted)
    .map((section) => {
      const changes = (section.changes ?? []).map(mapChange);
      // Verify flags are prompts to read the section, not proposed edits.
      const proposed = changes.filter((change) => !isVerifyFlag(change));
      const raw = section.sectionContent ?? '';
      const isHtml = looksLikeHtml(raw);
      const contentHtml = isHtml ? raw : toParagraphs(raw);
      return {
        id: section.id,
        sectionNumber: section.sectionNumber,
        sectionTitle: section.sectionTitle,
        sectionSequence: section.sectionSequence,
        parentSectionId: section.parentSectionId,
        changed: proposed.length > 0,
        changeCount: proposed.length,
        pendingCount: proposed.filter((change) => change.status === 'Pending').length,
        isEditable: true,
        contentHtml,
        previousContentHtml: contentHtml,
        changes,
      };
    })
    .sort((a, b) => a.sectionSequence - b.sectionSequence);
}

function mapChange(change: ReviewChangeDto): ChangeCandidate {
  return {
    id: change.changeId,
    sectionId: change.sectionId,
    changeType: humanize(change.candidateType),
    candidateType: change.candidateType ?? '',
    matchKind: mapMatchKind(change.source),
    // Not carried by the API — the confidence note and date flag stay hidden until it is.
    confidence: null,
    isDateFlagged: false,
    triggerReason: change.triggerReason ?? null,
    source: change.source,
    rationale: change.rationale ?? undefined,
    status: mapStatus(change.status),
    // Everything from the API is server-side, whatever its source says.
    isLocal: false,
    // Preserve both text and HTML fields for compatibility
    originalText: change.originalText ?? '',
    proposedText: change.proposedText ?? '',
    originalHtml: change.originalText ?? '',
    proposedHtml: change.proposedText ?? '',
    editedHtml: change.editedText,
    editedText: change.editedText,
    // Map provenances with all required fields for both old and new templates
    provenance: (change.provenances ?? []).map((item) => ({
      provenanceId: item.provenanceId,
      fileName: item.fileName ?? item.filename ?? '',
      sheetName: item.sheetName,
      sheet: item.sheetName,
      cell: item.cell,
      cellRef: item.cell,
      columnName: item.columnName,
      value: item.value,
      rawValue: item.value,
      fileId: item.provenanceId,
      verificationStatus: null,
    })),
    provenances: (change.provenances ?? []).map((item) => ({
      provenanceId: item.provenanceId,
      fileName: item.fileName ?? item.filename ?? '',
      sheetName: item.sheetName,
      sheet: item.sheetName,
      cell: item.cell,
      cellRef: item.cell,
      columnName: item.columnName,
      value: item.value,
      rawValue: item.value,
      fileId: item.provenanceId,
      verificationStatus: null,
    })),
  };
}

/** The API reports status in upper case ('EDITED'); the view model uses 'Edited'. */
export function mapStatus(status: string): ChangeStatus {
  switch ((status ?? '').toUpperCase()) {
    case 'ACCEPTED':
      return 'Accepted';
    case 'DECLINED':
    case 'REJECTED':
      return 'Declined';
    case 'EDITED':
      return 'Edited';
    default:
      return 'Pending';
  }
}

function mapMatchKind(source: string): MatchKind {
  const value = (source ?? '').toUpperCase();
  if (value.includes('USER') || value.includes('MANUAL')) return 'Manual';
  if (value.includes('HEURISTIC')) return 'Heuristic';
  return 'Exact';
}

/** PROPOSED_EDIT -> "Proposed edit" */
function humanize(value: string): string {
  if (!value) return '';
  const words = value.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * sectionContent arrives as plain text, so blank lines become paragraphs and single newlines
 * become breaks. Content that already carries markup is passed through untouched.
 */
function toParagraphs(text: string): string {
  return text
    .split(/\r?\n\s*\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\r?\n/g, '<br />')}</p>`)
    .join('');
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][^>]*>/i.test(value);
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
